import { zodResolver } from '@hookform/resolvers/zod';
import { PencilSimpleLine } from '@phosphor-icons/react';
import { MouseEvent } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';

import { FileOrFolder } from '../../TabDisplay';

import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from '~/components/ui/dialog';
import { InputField } from '~/components/ui/input';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { ReportService } from '~/services/api/reports/endpoints';
import { isMutationSuccess } from '~/services/api/rest-api';
import { useCurrentUser } from '~/services/api/users/endpoints';

const schema = z.object({
  folderName: z.string().min(1, 'Folder name is required'),
});

type FormFields = z.infer<typeof schema>;

interface CreateFolderModalProps {
  parentId: number | null | undefined;
  reportId: string | undefined;
  folderId: number | undefined | null;
  currentDirectoryItems: FileOrFolder[];
}

const RenameFolderModal = ({
  reportId,
  folderId,
  parentId,
  currentDirectoryItems,
}: CreateFolderModalProps) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const user = useCurrentUser();

  const form = useForm<FormFields>({
    defaultValues: {
      folderName: '',
    },
    resolver: zodResolver(schema),
  });

  const [renameFolder] =
    ReportService.endpoints.renameAndMoveFolderCasemanager.useMutation();

  const onSubmitHandler = form.handleSubmit(async (data) => {
    if (!reportId || !folderId || !user.data?.id) return;
    const folderNames = currentDirectoryItems
      .filter((item) => item.is_folder === true)
      .map((folder) => folder.name);
    const isDuplicate = folderNames.includes(data.folderName.trim());

    if (isDuplicate) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Folder name already exists in the current level.',
        }),
        'error',
      );
      return;
    }
    const result = await renameFolder({
      folder_id: folderId,
      name: data.folderName,
      uploaderID: user.data?.id,
      report: reportId,
      parent_folder: parentId,
    });

    if (isMutationSuccess(result)) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Folder renamed.',
        }),
        'success',
      );
      form.reset();
    } else {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'Something went wrong. Please try again later.',
        }),
        'error',
      );
    }
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant='soft'
          color='neutral'
          className='block !justify-start w-full !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            // handleOptionClick(i.id, 'Option 3');
          }}
          startDecorator={<PencilSimpleLine size={22} />}
        >
          <FormattedMessage defaultMessage={'Rename'} />
        </Button>
      </DialogTrigger>
      <DialogContent size='lg' className='mx-auto w-full h-50 relative'>
        <form onSubmit={onSubmitHandler}>
          <div className='text-lg font-bold mb-7'>
            <FormattedMessage defaultMessage='Rename' />
          </div>
          <InputField
            control={form.control}
            field='folderName'
            placeholder='Folder Name'
            size='lg'
            className='mt-3 mb-3'
            onKeyDown={handleKeyDown}
            onClick={handleClick}
          />

          <div className='flex justify-end items-end space-x-4'>
            <DialogClose asChild>
              <Button
                onClick={(event: MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  form.reset();
                  // handleOptionClick(i.id, 'Option 3');
                }}
                variant='outlined'
                color='neutral'
                className='text-base h-10'
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              className='bg-blue-600 h-10 hover:bg-blue-700 text-white font-bold rounded-full'
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
              }}
              type='submit'
            >
              <FormattedMessage defaultMessage={'Rename'} />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RenameFolderModal;
